/**
 * @fileoverview Shared module for query client with performance optimizations.
 *
 * IMPORTANT — DO NOT export singleton QueryClient instances from this file.
 * React Query explicitly documents that QueryClient instances must be created
 * inside React state (useState) so they are scoped to a component tree, not
 * shared across render trees. A module-level singleton survives workspace
 * switches and leaks stale tenant data.
 *
 * Usage:
 *   const [client] = useState(() => createQueryClient());
 *
 * Performance Features:
 * - Longer stale times to reduce API calls
 * - Request deduplication with longer gcTime
 * - Retry backoff for resilience
 * - No automatic refetch on window focus for editor routes
 */
import { QueryClient } from '@tanstack/react-query';

/**
 * Standard app query client factory.
 * Instantiate inside React useState — never at module scope.
 *
 * @example
 * const [queryClient] = useState(() => createQueryClient());
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // ✅ PERFORMANCE: Longer stale time reduces unnecessary API calls
        staleTime: 1000 * 60 * 5, // 5 minutes

        // ✅ PERFORMANCE: Longer gcTime enables request deduplication
        gcTime: 1000 * 60 * 30, // 30 minutes

        // ✅ PERFORMANCE: Don't refetch when browser tab regains focus
        refetchOnWindowFocus: false,

        // ✅ RESILIENCE: Single retry on failure
        retry: 1,

        networkMode: 'always',
      },
      mutations: {
        retry: 1,
        networkMode: 'always',
      },
    },
  });
}

/**
 * Editor-optimized query client factory.
 * Uses more aggressive caching for blocks, templates, and project data.
 * Instantiate inside React useState — never at module scope.
 *
 * @example
 * const [editorClient] = useState(() => createEditorQueryClient());
 */
export function createEditorQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // ✅ EDITOR OPTIMIZATION: Very long stale time in editor
        staleTime: 1000 * 60 * 10, // 10 minutes

        // ✅ EDITOR OPTIMIZATION: Very long cache time in editor
        gcTime: 1000 * 60 * 60, // 1 hour

        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: false,

        retry: 1,
        networkMode: 'always',
      },
      mutations: {
        retry: 1,
        networkMode: 'always',
      },
    },
  });
}
